import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getPublicRatgeber, getPublicRatgeberChapter, getSettings } from '../api';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import { SectionTitle } from '../components/Section';
import {
  BookOpen, ChevronRight, ArrowLeft, Search, Cat, Heart, Shield, X,
  Stethoscope, Scissors, TreePine, Baby, Users, Truck, Scale,
  Snowflake, ShoppingBag, Droplets, Home as HomeIcon, Sparkles, Star, PawPrint
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const chapterIcons = [Cat, HomeIcon, Droplets, Scissors, Stethoscope, TreePine, Shield, Baby, Sparkles, Users, Star, Truck, Scale, PawPrint, Heart, ShoppingBag];
const catLabels = { wasserfilter: 'Wasserfilter', nassfutter: 'Nassfutter', trockenfutter: 'Trockenfutter', kratzbaum: 'Kratzbäume', spielzeug: 'Spielzeug', pflegeprodukt: 'Pflegeprodukte', transportbox: 'Transportboxen', streu: 'Streu', sicherheitsnetz: 'Sicherheitsnetze' };

const RatgeberPage = ({ user, onLogout }) => {
  const [settings, setSettings] = useState({});
  const [chapters, setChapters] = useState([]);
  const [affiliates, setAffiliates] = useState([]);
  const [popup, setPopup] = useState(null);
  const [popupLoading, setPopupLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([getPublicRatgeber(), getSettings()])
      .then(([rRes, sRes]) => { setChapters(rRes.data.chapters); setAffiliates(rRes.data.affiliates); setSettings(sRes.data); })
      .catch(console.error).finally(() => setLoading(false));
  }, []);

  const openChapter = async (key) => {
    setPopupLoading(true);
    setPopup({ loading: true });
    try {
      const res = await getPublicRatgeberChapter(key);
      setPopup({ chapter: res.data.chapter, affiliates: res.data.affiliates });
    } catch (err) { setPopup(null); }
    finally { setPopupLoading(false); }
  };

  const filteredChapters = searchQuery.length >= 2
    ? chapters.filter(c => c.title?.toLowerCase().includes(searchQuery.toLowerCase()) || c.body?.toLowerCase().includes(searchQuery.toLowerCase()))
    : chapters;

  if (loading) {
    return <div className="flex items-center justify-center min-h-screen bg-[#EEF3F8]"><Snowflake size={48} className="text-[#7A9BB5] animate-spin" style={{ animationDuration: '3s' }} /></div>;
  }

  return (
    <div className="min-h-screen bg-[#EEF3F8]">
      <Navigation user={user} onLogout={onLogout} settings={settings} isLanding={false} />

      {/* Banner + Header */}
      <div className="relative">
        <div className="w-full h-40 md:h-56 overflow-hidden">
          <img src={settings.banner_landing_url ? `${BACKEND_URL}${settings.banner_landing_url}` : `${window.location.origin}/banner.jpg`}
            alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#2E3A46]/60 to-[#2E3A46]"></div>
        </div>
        <div className="absolute bottom-0 left-0 right-0 pb-6 md:pb-8">
          <div className="mx-auto w-full max-w-6xl px-4 sm:px-6">
            <Link to="/" className="inline-flex items-center gap-1.5 text-[#7A9BB5] hover:text-white text-sm mb-3"><ArrowLeft size={14} /> Startseite</Link>
            <h1 className="font-heading text-3xl md:text-5xl text-white">Ratgeber</h1>
          </div>
        </div>
      </div>
      <div className="bg-[#2E3A46] pb-8">
        <div className="mx-auto w-full max-w-6xl px-4 sm:px-6">
          <p className="text-[#C5D0DA] text-base md:text-lg max-w-2xl leading-relaxed">
            Umfassendes Wissen zur Haltung, Pflege, Gesundheit und Ernährung Ihrer Heiligen Birma – von erfahrenen Züchtern zusammengestellt.
          </p>
        </div>
      </div>

      <div className="mx-auto w-full max-w-6xl px-4 sm:px-6 py-8 md:py-12">
        {/* Search */}
        <div className="relative max-w-md mx-auto mb-10">
          <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#C5D0DA]" />
          <input type="text" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Kapitel durchsuchen... z.B. Futter, Impfung, Pflege"
            className="w-full pl-11 pr-4 py-3 rounded-xl border border-[#C5D0DA] bg-white text-[#2E3A46] text-sm focus:border-[#7A9BB5] focus:outline-none" />
        </div>

        {/* Chapters Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredChapters.map((ch, idx) => {
            const Icon = chapterIcons[idx % chapterIcons.length] || BookOpen;
            return (
              <button key={ch.key} onClick={() => openChapter(ch.key)}
                className="card-frost p-5 text-left hover:shadow-lg transition-all group hover:-translate-y-0.5 w-full">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-xl bg-[#D8E8F0] flex items-center justify-center shrink-0 group-hover:bg-[#7A9BB5] transition-colors">
                    <Icon size={20} className="text-[#7A9BB5] group-hover:text-white transition-colors" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <span className="text-xs font-bold text-[#C5D0DA]">{String(idx + 1).padStart(2, '0')}</span>
                    <h3 className="font-medium text-[#2E3A46] text-sm group-hover:text-[#7A9BB5] transition-colors">{ch.title}</h3>
                    <p className="text-xs text-[#7A9BB5] line-clamp-2 mt-1">{ch.body?.substring(0, 80)}...</p>
                  </div>
                  <ChevronRight size={16} className="text-[#C5D0DA] group-hover:text-[#7A9BB5] shrink-0 mt-2" />
                </div>
              </button>
            );
          })}
        </div>

        {/* Affiliate Section */}
        {affiliates.length > 0 && (
          <div className="mt-14">
            <SectionTitle subtitle="Produkte die wir empfehlen">Unsere Empfehlungen</SectionTitle>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {affiliates.map(aff => (
                <a key={aff.id} href={aff.affiliate_url || '#'} target="_blank" rel="noopener noreferrer"
                  className="card-frost p-5 hover:shadow-lg transition-shadow relative group">
                  <span className="absolute top-3 right-3 text-[10px] px-1.5 py-0.5 rounded bg-[#2E3A46]/70 text-white">Werbung</span>
                  <div className="w-10 h-10 rounded-xl bg-[#D8E8F0] flex items-center justify-center mb-3">
                    <ShoppingBag size={18} className="text-[#7A9BB5]" />
                  </div>
                  <h4 className="font-medium text-[#2E3A46] mb-1 group-hover:text-[#7A9BB5] pr-14">{aff.name}</h4>
                  <span className="text-xs px-2 py-0.5 rounded-full bg-[#D8E8F0] text-[#7A9BB5]">{catLabels[aff.category] || aff.category}</span>
                  {aff.description && <p className="text-sm text-[#354551] mt-2 line-clamp-2">{aff.description}</p>}
                </a>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Chapter Popup */}
      {popup && (
        <div className="fixed inset-0 bg-[#2E3A46]/80 z-[60] flex items-start justify-center p-4 pt-16 md:pt-20 overflow-y-auto" onClick={() => setPopup(null)}>
          <div className="bg-white rounded-2xl max-w-3xl w-full shadow-2xl animate-fade-in mb-8" onClick={(e) => e.stopPropagation()}>
            {popup.loading ? (
              <div className="p-12 text-center"><Snowflake size={32} className="text-[#7A9BB5] mx-auto animate-spin" style={{ animationDuration: '2s' }} /></div>
            ) : popup.chapter ? (
              <>
                {/* Header */}
                <div className="bg-gradient-to-r from-[#2E3A46] to-[#354551] p-6 md:p-8 rounded-t-2xl relative">
                  <button onClick={() => setPopup(null)} className="absolute top-4 right-4 w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white">
                    <X size={18} />
                  </button>
                  <h2 className="font-heading text-2xl md:text-3xl text-white pr-10">{popup.chapter.title}</h2>
                </div>

                {/* Images top */}
                {popup.chapter.images?.length > 0 && popup.chapter.image_position === 'top' && (
                  <div className={popup.chapter.images.length === 1 ? '' : 'grid grid-cols-2 gap-1'}>
                    {popup.chapter.images.map((url, i) => <img key={i} src={`${BACKEND_URL}${url}`} alt="" className="w-full h-48 md:h-64 object-cover" />)}
                  </div>
                )}

                {/* Content */}
                <div className="p-6 md:p-10">
                  {popup.chapter.images?.length > 0 && (popup.chapter.image_position === 'left' || popup.chapter.image_position === 'right') ? (
                    <div className={`flex flex-col md:flex-row gap-6 ${popup.chapter.image_position === 'right' ? 'md:flex-row-reverse' : ''}`}>
                      <div className="md:w-2/5 shrink-0 space-y-2">
                        {popup.chapter.images.map((url, i) => <img key={i} src={`${BACKEND_URL}${url}`} alt="" className="w-full rounded-xl object-cover" />)}
                      </div>
                      <div className="flex-1 text-[#354551] leading-relaxed whitespace-pre-line">{popup.chapter.body}</div>
                    </div>
                  ) : (
                    <div className="text-[#354551] leading-relaxed whitespace-pre-line">{popup.chapter.body}</div>
                  )}
                </div>

                {/* Affiliates in popup */}
                {popup.affiliates?.length > 0 && (
                  <div className="px-6 md:px-10 pb-6 md:pb-10 border-t border-[#C5D0DA]/30 pt-6">
                    <h4 className="font-heading text-lg text-[#2E3A46] mb-3">Passende Empfehlungen</h4>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {popup.affiliates.slice(0, 4).map(aff => (
                        <a key={aff.id} href={aff.affiliate_url || '#'} target="_blank" rel="noopener noreferrer"
                          className="p-3 rounded-xl border border-[#C5D0DA] hover:bg-[#EEF3F8] transition-colors relative group">
                          <span className="absolute top-2 right-2 text-[9px] px-1 py-0.5 rounded bg-[#2E3A46]/60 text-white">Werbung</span>
                          <h5 className="font-medium text-sm text-[#2E3A46] group-hover:text-[#7A9BB5] pr-12">{aff.name}</h5>
                          <span className="text-[10px] text-[#7A9BB5]">{catLabels[aff.category] || aff.category}</span>
                        </a>
                      ))}
                    </div>
                  </div>
                )}
              </>
            ) : null}
          </div>
        </div>
      )}

      <Footer settings={settings} />
    </div>
  );
};

export default RatgeberPage;
